import React, { useState, useEffect } from 'react';
import { X, GitMerge, Check } from 'lucide-react';
import api from '../../utils/api';

const FIELDS = [
  { key: 'name', label: 'שם מלא' },
  { key: 'email', label: 'אימייל', ltr: true },
  { key: 'city', label: 'עיר' },
  { key: 'address', label: 'כתובת' },
  { key: 'notes', label: 'הערות' },
];

function SelCell({ value, active, onClick, ltr }) {
  return (
    <button
      type="button"
      onClick={onClick}
      dir={ltr ? 'ltr' : undefined}
      className={`w-full text-right px-3 py-2 rounded-lg border text-sm transition-colors ${
        active
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-gray-900 dark:text-white ring-2 ring-primary-300'
          : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:border-gray-300'
      }`}
    >
      {value || <span className="text-gray-400 italic text-xs" dir="rtl">ריק</span>}
    </button>
  );
}

export default function CustomerMergeModal({ ids, onClose, onMerged }) {
  // ids: [id1, id2]
  const [customers, setCustomers] = useState([null, null]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // primaryIdx: which customer keeps the ID (0 or 1)
  const [primaryIdx, setPrimaryIdx] = useState(0);

  // sel[field] = 0 or 1 — which customer's value to use
  const [sel, setSel] = useState({});

  // selectedPhones: Set of normalized phone strings to keep
  const [allPhones, setAllPhones] = useState([]);
  const [selectedPhones, setSelectedPhones] = useState(new Set());

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [r0, r1] = await Promise.all([
          api.get(`/customers/${ids[0]}`),
          api.get(`/customers/${ids[1]}`),
        ]);
        const c0 = r0.data;
        const c1 = r1.data;
        setCustomers([c0, c1]);

        // Default field selection: prefer non-empty from customer 0
        const defaultSel = {};
        for (const { key } of FIELDS) {
          defaultSel[key] = c0[key] ? 0 : 1;
        }
        setSel(defaultSel);

        // Merge phones list, dedupe by normalized value
        const seen = new Set();
        const merged = [];
        for (const p of [...(c0.phones || []), ...(c1.phones || [])]) {
          if (!seen.has(p.phone)) {
            seen.add(p.phone);
            merged.push(p);
          }
        }
        setAllPhones(merged);
        setSelectedPhones(new Set(merged.map((p) => p.phone)));
      } catch {
        setError('שגיאה בטעינת נתוני לקוחות');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [ids[0], ids[1]]);

  function togglePhone(phone) {
    setSelectedPhones((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  }

  const merged = customers[0] && customers[1]
    ? Object.fromEntries(FIELDS.map(({ key }) => [key, customers[sel[key] ?? 0]?.[key] ?? '']))
    : {};

  async function handleSubmit(e) {
    e.preventDefault();
    if (!customers[0] || !customers[1]) return;
    setSaving(true);
    setError('');
    try {
      const primaryId = customers[primaryIdx].id;
      const secondaryId = customers[1 - primaryIdx].id;
      await api.post('/customers/merge', {
        primary_id: primaryId,
        secondary_id: secondaryId,
        fields: merged,
        phones: [...selectedPhones],
      });
      onMerged();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה במיזוג');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <GitMerge size={18} className="text-primary-600 dark:text-primary-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">מיזוג לקוחות</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin h-7 w-7 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : error && !customers[0] ? (
          <div className="p-6 text-center text-red-500 text-sm">{error}</div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Primary selection */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                איזה לקוח ישמור את המזהה שלו? (הלקוח שישרוד)
              </p>
              <div className="grid grid-cols-2 gap-3">
                {customers.map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setPrimaryIdx(i)}
                    className={`px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors text-right ${
                      primaryIdx === i
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-2 ring-primary-300'
                        : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    {primaryIdx === i && <Check size={13} className="inline ml-1" />}
                    {c.name || 'ללא שם'}{' '}
                    <span className="text-xs text-gray-400 font-normal">#{c.id}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Column headers */}
            <div>
              <div className="grid grid-cols-[120px_1fr_1fr] gap-3 mb-2">
                <div />
                {customers.map((c, i) => (
                  <div key={c.id} className="text-xs font-semibold text-gray-500 dark:text-gray-400 truncate px-1">
                    {c.name || 'ללא שם'} <span className="font-normal opacity-60">#{c.id}</span>
                  </div>
                ))}
              </div>

              {/* Field rows */}
              <div className="space-y-2">
                {FIELDS.map(({ key, label, ltr }) => (
                  <div key={key} className="grid grid-cols-[120px_1fr_1fr] gap-3 items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">{label}</span>
                    {customers.map((c, i) => (
                      <SelCell
                        key={i}
                        value={c[key]}
                        active={sel[key] === i}
                        onClick={() => setSel((s) => ({ ...s, [key]: i }))}
                        ltr={ltr}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Phones */}
            {allPhones.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  מספרי טלפון לשמירה (סמן את מה שרוצים לשמור):
                </p>
                <div className="flex flex-wrap gap-2">
                  {allPhones.map((p) => {
                    const checked = selectedPhones.has(p.phone);
                    return (
                      <button
                        key={p.phone}
                        type="button"
                        onClick={() => togglePhone(p.phone)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-mono transition-colors ${
                          checked
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                            : 'border-gray-200 dark:border-gray-600 text-gray-400 line-through'
                        }`}
                      >
                        {checked && <Check size={10} />}
                        <span dir="ltr">{p.phone}</span>
                        {p.label && <span className="font-sans opacity-70">{p.label}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Preview */}
            <div className="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10 p-4">
              <p className="text-xs font-semibold text-primary-700 dark:text-primary-300 mb-3 uppercase tracking-wide">
                תצוגה מקדימה — כרטיס לקוח סופי
              </p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                {FIELDS.map(({ key, label, ltr }) => (
                  <div key={key} className="flex gap-2">
                    <span className="text-gray-500 dark:text-gray-400 min-w-16">{label}:</span>
                    <span className="text-gray-900 dark:text-white font-medium" dir={ltr ? 'ltr' : undefined}>
                      {merged[key] || <span className="text-gray-400 italic text-xs" dir="rtl">ריק</span>}
                    </span>
                  </div>
                ))}
                <div className="flex gap-2 col-span-2">
                  <span className="text-gray-500 dark:text-gray-400 min-w-16">טלפונים:</span>
                  <span className="text-gray-900 dark:text-white font-mono text-xs" dir="ltr">
                    {[...selectedPhones].join(', ') || <span className="text-gray-400 italic font-sans">ללא</span>}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                ביטול
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
                <GitMerge size={15} />
                {saving ? 'ממזג...' : 'מזג לקוחות'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
